import { useNavigate } from "react-router-dom";
import { USE_CASES } from "@/data/useCases";
import UploadDropzone from "@/components/UploadDropzone";
import { ArrowUpRight, ShieldCheck, Zap, FileSearch } from "lucide-react";
import { useCallback } from "react";

export default function HomePage() {
  const navigate = useNavigate();

  const onFile = useCallback(
    (file) => {
      const dt = new DataTransfer();
      dt.items.add(file);
      // hand off via sessionStorage bridge (files can't survive nav) — we use a file store
      window.__PDF_REVIEW_PENDING_FILE__ = file;
      navigate("/workspace");
    },
    [navigate]
  );

  return (
    <>
      {/* HERO */}
      <section
        data-testid="home-hero"
        className="max-w-[1280px] mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16"
      >
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow mb-5">PDF-review.com</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl md:text-6xl leading-[1.03] text-ink">
              Review the right way for this PDF.
            </h1>
            <p className="mt-6 text-base md:text-lg text-ink-70 leading-relaxed max-w-lg">
              Upload a document. We detect the type and run the matching review playbook —
              NDAs, MSAs, leases, invoices, resumes, and more. Structured findings, quoted
              from your PDF, in under two minutes.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-5 text-sm text-ink-70">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-forest" />
                <span>No auto-send, no auto-share</span>
              </div>
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-forest" />
                <span>Under 2 minutes</span>
              </div>
              <div className="flex items-center gap-2">
                <FileSearch className="w-4 h-4 text-forest" />
                <span>Cites the exact clause</span>
              </div>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {[
                { n: "6", l: "Review playbooks" },
                { n: "≤2m", l: "Time to review" },
                { n: "100%", l: "Cited from your doc" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 border-ink pl-3">
                  <div className="font-serif-display text-2xl text-ink">{s.n}</div>
                  <div className="text-xs text-ink-50 mt-1 font-mono-plex uppercase tracking-wider">
                    {s.l}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="md:col-span-7">
            <UploadDropzone onFile={onFile} testId="home-dropzone" />
            <p className="mt-4 text-xs text-ink-50 font-mono-plex tracking-wider uppercase">
              No account required to try. Guest mode runs one review.
            </p>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 border-t border-ink-10">
        <div className="eyebrow mb-5">How it works</div>
        <h2 className="font-serif-display text-3xl md:text-4xl text-ink max-w-2xl leading-tight">
          A PDF tool that picks the right review — not a chatbot.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-14">
          {[
            {
              n: "01",
              t: "Upload & classify",
              d: "Drag in a PDF or DOCX. We detect the document type with a confidence score. You confirm, or switch the type before running.",
            },
            {
              n: "02",
              t: "Run the matching playbook",
              d: "Every type has its own review lens — liability caps for MSAs, surprise fees for leases, math checks for invoices.",
            },
            {
              n: "03",
              t: "Read the review pack",
              d: "A prioritized red/amber/green issue list with exact quotes and page citations, plus a client-style memo you can copy.",
            },
          ].map((s) => (
            <div key={s.n} className="border-t border-ink pt-6">
              <div className="font-mono-plex text-xs text-ink-50 tracking-widest uppercase">
                Step {s.n}
              </div>
              <div className="mt-3 font-serif-display text-2xl text-ink">{s.t}</div>
              <p className="mt-3 text-sm text-ink-70 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* USE CASES */}
      <section
        id="use-cases"
        className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 border-t border-ink-10"
      >
        <div className="flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="eyebrow mb-4">Playbooks</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink max-w-2xl leading-tight">
              Pick the right review for this PDF.
            </h2>
          </div>
          <button
            className="btn-outline focus-ring"
            data-testid="home-explore-usecases-btn"
            onClick={() => navigate("/use-cases")}
          >
            Explore all <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border border-ink-10">
          {USE_CASES.map((u, i) => (
            <button
              key={u.key}
              data-testid={`home-usecase-${u.key}`}
              onClick={() => navigate(`/use-cases/${u.key}`)}
              className={`text-left p-8 bg-surface border-ink-10 hover:bg-[#FAFAF8] transition-colors group ${
                i % 3 !== 2 ? "lg:border-r" : ""
              } ${i % 2 !== 1 ? "md:border-r lg:border-r" : ""} ${
                i < USE_CASES.length - (USE_CASES.length % 3 || 3)
                  ? "lg:border-b"
                  : ""
              } border-b md:border-b`}
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-mono-plex text-xs text-ink-50 tracking-widest">
                  {u.number}
                </span>
                <span className="font-mono-plex text-xs text-ink-50 uppercase tracking-widest">
                  {u.tag}
                </span>
              </div>
              <div className="font-serif-display text-2xl text-ink mb-3 group-hover:text-forest transition-colors">
                {u.label}
              </div>
              <div className="text-sm text-ink-70 mb-5 leading-relaxed">{u.for_who}</div>
              <div className="flex items-center gap-2 text-xs font-mono-plex text-forest uppercase tracking-widest">
                Open playbook <ArrowUpRight className="w-3.5 h-3.5" />
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* SOCIAL PROOF / TRUST */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 border-t border-ink-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6">
            <div className="eyebrow mb-4">Built for real work</div>
            <h2 className="font-serif-display text-3xl md:text-4xl text-ink leading-tight">
              A calm document tool, not a neon AI toy.
            </h2>
            <p className="mt-6 text-ink-70 leading-relaxed">
              PDF Review is opinionated: every review type has a hand-crafted checklist,
              every finding cites the source text, and nothing is auto-sent. You stay in
              control of the redline and the reply.
            </p>
            <ul className="mt-8 space-y-4 text-sm text-ink-70">
              {[
                "Every finding quotes the exact clause and page.",
                "Review packs are copy-ready — memo, issues, exports.",
                "History saves every review to your account.",
                "Never auto-shares your document with anyone.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-forest mt-2" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-6">
            <div className="paper-card">
              <div className="eyebrow mb-4">Example finding</div>
              <div className="font-serif-display text-xl text-ink">
                Auto-renewal with silent 90-day rollover
              </div>
              <div className="mt-3 flex gap-2">
                <span className="pill pill-red">
                  <span className="dot" /> red
                </span>
                <span className="font-mono-plex text-xs text-ink-50 uppercase tracking-widest self-center">
                  p.12
                </span>
              </div>
              <div className="quote-block mt-4">
                "This Agreement shall automatically renew for successive one-year terms
                unless either party gives written notice at least ninety (90) days prior…"
              </div>
              <div className="mt-4 text-sm text-ink-70">
                <span className="text-ink font-medium">Why it matters:</span> a 90-day
                notice window is longer than typical 30–60 days and easy to miss.
              </div>
              <div className="mt-3 text-sm text-ink-70">
                <span className="text-ink font-medium">Action:</span> shorten to 30 days or
                add a calendar reminder tied to the renewal date.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-[1280px] mx-auto px-6 md:px-10 py-16 border-t border-ink-10">
        <div className="bg-ink text-white p-10 md:p-14 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="font-mono-plex text-xs uppercase tracking-widest text-white/60 mb-3">
              Start reviewing
            </div>
            <div className="font-serif-display text-3xl md:text-4xl leading-tight">
              Your next contract, one drop away.
            </div>
          </div>
          <button
            data-testid="home-cta-btn"
            className="bg-white text-ink px-8 py-4 font-medium hover:bg-[#F0F7F3] transition-colors focus-ring"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          >
            Upload a document
          </button>
        </div>
        <p className="mt-6 text-xs text-ink-50 font-mono-plex uppercase tracking-widest text-center">
          This is document review assistance — not legal, tax, or professional advice.
        </p>
      </section>
    </>
  );
}
